import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { usePermissions } from '../../hooks/usePermissions';
import { useAdmin } from '../../hooks/useAdmin';
import { NavLink, useNavigate } from 'react-router-dom';
import { draftService } from '../../services/draftService';

export default function Sidebar() {
  const { user } = useAuth();
  const { data: userData } = useCurrentUser();
  const { isSurgeon } = usePermissions();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const [draftsCount, setDraftsCount] = useState(0);

  // Load drafts count
  useEffect(() => {
    const loadDraftsCount = async () => {
      try {
        const [drafts, autoSave] = await Promise.all([
          draftService.getDrafts(),
          draftService.getAutoSave()
        ]);
        setDraftsCount(drafts.length + (autoSave ? 1 : 0));
      } catch (error) {
        console.error('Failed to load drafts count:', error);
      }
    };

    if (isSurgeon) {
      loadDraftsCount();
    }
  }, [isSurgeon]);


  const getUserInitials = (name?: string) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const displayName = userData?.fullName || userData?.username || user?.name || 'User Name';
  const initials = getUserInitials(displayName);
  const profilePicture = userData?.profilePicture || user?.avatar;

  return (
    <div className="w-64 theme-card shadow-sm p-6">
      {/* Profile Card - Click to Edit */}
      <button
        onClick={() => navigate('/profile/edit')}
        className="w-full text-center mb-4 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        {/* Avatar */}
        <div className="h-20 w-20 mx-auto mb-3">
          {profilePicture ? (
            <img
              src={profilePicture}
              alt={displayName}
              className="h-20 w-20 rounded-full object-cover"
            />
          ) : (
            <div className="h-20 w-20 bg-gradient-to-br from-healthy-green to-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white text-2xl font-bold">
                {initials}
              </span>
            </div>
          )}
        </div>

        {/* Username */}
        <h3 className="text-lg font-semibold theme-text mb-1 flex items-center justify-center gap-2">
          {userData?.username || user?.username || displayName}
          <svg className="w-4 h-4 text-healthy-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </h3>
      </button>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
        <div className="space-y-2">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `block w-full text-left text-sm py-1 transition-colors ${
                isActive
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:text-primary-600'
              }`
            }
          >
            🏠 Feed
          </NavLink>
          {isSurgeon && (
            <NavLink
              to="/my-posts"
              className={({ isActive }) =>
                `block w-full text-left text-sm py-1 transition-colors ${
                  isActive
                    ? 'text-primary-600 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600'
                }`
              }
            >
              📝 My posts
            </NavLink>
          )}
          <NavLink
            to="/saved-posts"
            className={({ isActive }) =>
              `block w-full text-left text-sm py-1 transition-colors ${
                isActive
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:text-primary-600'
              }`
            }
          >
            🔖 Saved posts
          </NavLink>
          {isSurgeon && (
            <NavLink
              to="/drafts"
              className={({ isActive }) =>
                `block w-full text-left text-sm py-1 transition-colors ${
                  isActive
                    ? 'text-primary-600 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600'
                }`
              }
            >
              📝 Drafts {draftsCount > 0 && <span className="text-xs opacity-60">({draftsCount})</span>}
            </NavLink>
          )}
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `block w-full text-left text-sm py-1 transition-colors ${
                isActive
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:text-primary-600'
              }`
            }
          >
            ⚙️ Settings
          </NavLink>
        </div>
      </div>

      {/* ActiveIntelligence Logo */}
      <div className="border-t border-gray-200 dark:border-gray-700 mt-auto pt-4">
        <div className="flex items-end justify-end p-2">
          <img
            src="/activeintelligence-logo.png"
            alt="ActiveIntelligence"
            className="w-32 h-auto"
          />
        </div>
      </div>

      {/* Admin Section - Only show for admin users */}
      {isAdmin && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
          <h4 className="text-sm font-medium theme-text mb-3 flex items-center">
            <span className="p-1 bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 rounded mr-2 text-xs">
              🛡️
            </span>
            Admin Panel
          </h4>
          <div className="space-y-2">
            <NavLink
              to="/admin"
              className="block w-full text-left text-sm py-1 transition-colors text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
            >
              🔧 Admin Dashboard
            </NavLink>
          </div>
        </div>
      )}
    </div>
  );
}
